import { Footer } from '@/shared/ui/components';
import type { TableProps } from 'antd';
import { Col, Row, Table } from 'antd';
import _ from 'lodash';
import { t } from 'ttag';
import { useDiagnosticYield } from './useDiagnosticYield';
import BiocareLogo from './assets/biocare-logo.svg';
import type { DataType, LinkType } from '../../model/diagnosticYield.types';
import './diagnosticYield.scss';
import {
  inititalDataTable1,
  inititalDataTable2,
  referenceList,
} from '../../model/diagnosticYieldInitialData';

/**
 * Renders the static diagnostic yield reference page, arguing that yield improves with studies of at least ten days. Builds one shared column set that renders each arrhythmia row as N, DY percent to two decimals and its 95% confidence interval across the AF, PVC, nsVT, VT, AVB, pause and bradycardia measures, then prints it as the women's and men's tables with their abbreviation notes, the external reference links and the page footer.
 *
 * @returns The diagnostic yield page markup.
 */
const DiagnosticYield = () => {
  useDiagnosticYield();

  const dataTable1: DataType[] = inititalDataTable1;
  const dataTable2: DataType[] = inititalDataTable2;
  const referenceLinks: LinkType[] = referenceList;

  const columns: TableProps<DataType>['columns'] = [
    {
      title: t`Arrhythmia`,
      colSpan: 1,
      render: (text, record) => (
        <Row>
          <Col span={16} className="col-3-row">
            <Row>
              <Col span={24}>{record.arrhythmiaSession}</Col>
            </Row>
          </Col>
          <Col span={8} className="divide-row">
            <Row>
              <Col span={24}>N</Col>
              <Col span={24}>DY, %</Col>
              <Col span={24}>95% Cl</Col>
            </Row>
          </Col>
        </Row>
      ),
    },
    {
      title: 'AF ≤1%',
      className: 'divide-row',
      colSpan: 1,
      key: 'af1',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.af1.n}</Col>
          <Col span={24}>{record.af1.dy.toFixed(2)}</Col>
          <Col span={24}>{record.af1.cl}</Col>
        </Row>
      ),
    },
    {
      title: 'AF ≤10%',
      className: 'divide-row',
      colSpan: 1,
      key: 'af10',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.af10.n}</Col>
          <Col span={24}>{record.af10.dy.toFixed(2)}</Col>
          <Col span={24}>{record.af10.cl}</Col>
        </Row>
      ),
    },
    {
      title: 'PVCs',
      className: 'divide-row',
      colSpan: 1,
      key: 'pvcs',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.pvcs.n}</Col>
          <Col span={24}>{record.pvcs.dy.toFixed(2)}</Col>
          <Col span={24}>{record.pvcs.cl}</Col>
        </Row>
      ),
    },
    {
      title: 'nsVT',
      className: 'divide-row',
      colSpan: 1,
      key: 'nsvt',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.nsvt.n}</Col>
          <Col span={24}>{record.nsvt.dy.toFixed(2)}</Col>
          <Col span={24}>{record.nsvt.cl}</Col>
        </Row>
      ),
    },
    {
      title: 'VT ≥30 s',
      className: 'divide-row',
      colSpan: 1,
      key: 'vt30',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.vt30.n}</Col>
          <Col span={24}>{record.vt30.dy.toFixed(2)}</Col>
          <Col span={24}>{record.vt30.cl}</Col>
        </Row>
      ),
    },
    {
      title: 'AVB',
      className: 'divide-row',
      colSpan: 1,
      key: 'avb',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.avb.n}</Col>
          <Col span={24}>{record.avb.dy.toFixed(2)}</Col>
          <Col span={24}>{record.avb.cl}</Col>
        </Row>
      ),
    },
    {
      title: t`Pause`,
      className: 'divide-row',
      colSpan: 1,
      key: 'pause',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.pause.n}</Col>
          <Col span={24}>{record.pause.dy.toFixed(2)}</Col>
          <Col span={24}>{record.pause.cl}</Col>
        </Row>
      ),
    },
    {
      title: t`Bradycardia`,
      className: 'divide-row',
      colSpan: 1,
      key: 'bradycardia',
      render: (text, record) => (
        <Row>
          <Col span={24}>{record.bradycardia.n}</Col>
          <Col span={24}>{record.bradycardia.dy.toFixed(2)}</Col>
          <Col span={24}>{record.bradycardia.cl}</Col>
        </Row>
      ),
    },
  ];

  return (
    <div className="container-diagnostic-yield">
      <div className="content">
        <div className="content__logo">
          <img src={BiocareLogo} alt="biocare logo" />
        </div>
        <div className="content__title">
          {t`Studies show that diagnostic yield is significantly improved with studies that are at least 10 days long.`}
        </div>
        <div className="content__table">
          <div className="table__title">
            <div className="table-number">
              {`${t`Table`} 1.`}{' '}
              <div className="table-name">{t`Diagnostic yield in women calculated after 24 hours, 48 hours, 5, 7, and 14 days`}</div>
            </div>
          </div>
          <Table
            aria-label={t`Diagnostic yield in women`}
            columns={columns}
            dataSource={dataTable1}
            bordered
          />
          <div className="table__sub-title">
            {t`Abbreviations: ABV, atrioventricular blocks; AF, atrial fibrillation; CI, confidence interval; DY, diagnostic yield; nsVT, non-sustained ventricular tachycardias; PVC, premature ventricular contractions; VT, ventricular tachycardias`}
          </div>
          <div className="table__title">
            <div className="table-number">
              {`${t`Table`} 2.`}{' '}
              <div className="table-name">{t`Diagnostic yield in men calculated after 24 hours, 48 hours, 5, 7, and 14 days`}</div>
            </div>
          </div>
          <Table
            aria-label={t`Diagnostic yield in men`}
            columns={columns}
            dataSource={dataTable2}
            bordered
          />
          <div className="table__sub-title">{t`Abbreviations: see Table 1`}</div>
        </div>
        <div className="content__divider" />
        <div className="content__references">
          <div className="references__title">{t`References`}</div>
          {_.map(referenceLinks, (ref) => (
            <div className="references__links">
              <a target="_blank" href={ref.url} rel="noreferrer">
                {ref.title}
              </a>{' '}
              <div className="sub-link">{ref.subTitle}</div>
            </div>
          ))}
        </div>
        <div className="content__divider" />
        <div className="content__footer">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default DiagnosticYield;
